import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CaretLeft,
  Clock,
  CaretDown,
  ArrowsDownUp,
  Microphone,
  Keyboard,
  SpeakerHigh,
  BookmarkSimple,
  PaperPlaneRight,
  X,
  CookingPot,
  Stop,
  ArrowRight,
  Camera,
  CircleNotch,
  MagicWand,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { usePaywall } from "../../lib/paywall";
import { useAuth } from "../../lib/auth";
import { LANGUAGES } from "../../lib/translatorLangs";
import {
  getCached,
  setCached,
  toggleFavorite,
  isFavorite,
  isValidTranslation,
  clearCachedAudio,
} from "../../lib/phrasebook";
import { isSeedPhrase } from "../../lib/seedPhrasebook";
import { getOfflineTranslation } from "../../lib/offlinePhrasebook";
import { useVoiceRecorder } from "../translator/useVoiceRecorder";
import PhraseBook from "../PhraseBook";
import MenuOverlay from "../menu/MenuOverlay";
import MenuFallbackRow from "../menu/MenuFallbackRow";
import { resizeToDataUrl } from "../../lib/imageUtils";
import { addHistory, listHistory } from "../../lib/historyLog";
import { getTtsSpeed } from "../../lib/ttsSpeed";
import { getOpenAIVoiceId, getTtsVoice, getTtsPlaybackMode, subscribeTtsVoiceChange } from "../../lib/ttsVoice";
import { speakNative, stopNativeTTS } from "../../lib/nativeTTS";
import HistorySheet from "./HistorySheet";

/**
 * Translate screen — pixel match to mockup. WHITE background, underlined
 * language pickers (no card chrome), italic "menu" word, sample
 * conversation bubbles preloaded, large orange mic CTA at the bottom.
 */
export default function TranslatePanel({ onBack, autoScanMenu = false, autoOpenPhrasebook = false }) {
  // Last-used language pair is persisted across app sessions so a user
  // travelling in Japan doesn't have to re-pick "Japanese" every time
  // they open Translate. Reads happen lazily inside useState so we
  // only touch localStorage once on mount.
  const [myLang, setMyLang] = useState(() => {
    try {
      return localStorage.getItem("tiplingo_my_lang") || "English";
    } catch {
      return "English";
    }
  });
  const [theirLang, setTheirLang] = useState(() => {
    try {
      return localStorage.getItem("tiplingo_their_lang") || "Spanish";
    } catch {
      return "Spanish";
    }
  });
  // Persist on change. Writes are guarded so a private-mode / quota
  // error doesn't crash the component.
  useEffect(() => {
    try { localStorage.setItem("tiplingo_my_lang", myLang); } catch { /* no-op */ }
  }, [myLang]);
  useEffect(() => {
    try { localStorage.setItem("tiplingo_their_lang", theirLang); } catch { /* no-op */ }
  }, [theirLang]);

  const [activeSide, setActiveSide] = useState("me");
  const [provider] = useState("gemini");
  // Bubble list is populated below by an effect that filters history to
  // the CURRENT language pair on mount AND whenever the user changes
  // languages before sending. Starting empty keeps the useState
  // initializer pure (no dependency on other state values).
  const [messages, setMessages] = useState([]);
  const [hasUserSent, setHasUserSent] = useState(false);

  // When the user changes the language pair, filter the conversation to
  // only entries that match the new pair. This handles BOTH the "no
  // messages yet" case (refresh seed bubbles from history) AND the
  // "has-sent messages" case where older bubbles in a different
  // language pair would otherwise stay on screen and confuse the user
  // (e.g. tapping "Korean" after translating to Japanese would still
  // show Japanese bubbles on screen).
  useEffect(() => {
    setMessages((prev) => {
      if (!prev || prev.length === 0) {
        return recentBubblesFromHistory(2, myLang, theirLang);
      }
      const src = (myLang || "").toLowerCase();
      const tgt = (theirLang || "").toLowerCase();
      const filtered = prev.filter((m) => {
        const ms = (m.src || "").toLowerCase();
        const mt = (m.tgt || "").toLowerCase();
        // Accept the pair in either direction (Me ↔ Them flip).
        return (ms === src && mt === tgt) || (ms === tgt && mt === src);
      });
      // If filtering wiped every bubble and the user hasn't sent
      // anything yet, fall back to seed bubbles for the NEW pair.
      if (filtered.length === 0 && !hasUserSent) {
        return recentBubblesFromHistory(2, myLang, theirLang);
      }
      return filtered;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myLang, theirLang]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [favTick, setFavTick] = useState(0);

  // Menu-scan state
  const [menuScanning, setMenuScanning] = useState(false);
  const [menuResult, setMenuResult] = useState(null);
  const [menuImgUrl, setMenuImgUrl] = useState(null);
  const [menuHoverIdx, setMenuHoverIdx] = useState(null);
  const [menuPinnedIdx, setMenuPinnedIdx] = useState(null);
  const menuFileRef = useRef(null);

  const { handle402 } = usePaywall();
  const { refresh: refreshAuth, usage, user } = useAuth();
  // Computed daily quota for the "X / Y LEFT TODAY" badge on the menu
  // scanner CTA. Reads the SAME backend /usage data the paywall uses
  // so the badge never lies. Pro users see "Pro access"; free/trial
  // users see the actual remaining count. Falls back to the documented
  // free-tier menu limit (2/day) when /usage hasn't hydrated yet —
  // better to show '2 / 2 LEFT TODAY' on first paint than a useless
  // em-dash placeholder that confuses anonymous first-time visitors.
  const FREE_MENU_LIMIT = 2;
  const FREE_TRANSLATE_LIMIT = 10;
  const menuLimit = usage?.limits?.menu ?? null;
  const menuUsed = usage?.used?.menu ?? 0;
  const menuQuotaLabel = (() => {
    if (user?.plan === "pro") return "Pro access";
    if (menuLimit == null) {
      // /usage not yet hydrated AND user isn't pro — assume the
      // free-tier limit so the badge reads correctly on first paint.
      return `${FREE_MENU_LIMIT} / ${FREE_MENU_LIMIT} left today`;
    }
    const remaining = Math.max(0, menuLimit - menuUsed);
    return `${remaining} / ${menuLimit} left today`;
  })();
  // Same idea for TEXT translation quota. Surfaces the user's
  // remaining free translations in the conversation header so they
  // never tap Send and get surprised by the paywall — instead they
  // see "5 / 10 left today" counting down and can plan accordingly.
  // Bound to /usage data the same way menuQuotaLabel is.
  const translateLimit = usage?.limits?.translate_text ?? null;
  const translateUsed = usage?.used?.translate_text ?? 0;
  const translateRemaining = (() => {
    if (user?.plan === "pro") return Infinity;
    if (translateLimit == null) return FREE_TRANSLATE_LIMIT;
    return Math.max(0, translateLimit - translateUsed);
  })();
  const translateLimitReached = translateRemaining <= 0;
  const translateQuotaLabel = (() => {
    if (user?.plan === "pro") return "Pro access";
    const limit = translateLimit ?? FREE_TRANSLATE_LIMIT;
    return `${translateRemaining} / ${limit} left today`;
  })();
  const audioElRef = useRef(null);
  // iOS WKWebView / mobile Safari require the FIRST .play() of an Audio
  // element to happen inside a user gesture. When tts is fetched async
  // (history bubbles, phrasebook, retry), the awaited fetch breaks the
  // gesture context and play() silently fails. We "unlock" the Audio
  // element once on first interaction by playing a silent buffer
  // synchronously inside the gesture — after that, programmatic plays
  // work for the rest of the session.
  const audioUnlockedRef = useRef(false);
  const sessionIdRef = useRef(null);
  const scrollerRef = useRef(null);

  // Auto-trigger the file picker when arriving from the "Scan menu" home shortcut.
  const autoTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoScanMenu && !autoTriggeredRef.current) {
      autoTriggeredRef.current = true;
      // Small timeout so the panel mounts before opening the picker
      setTimeout(() => menuFileRef.current?.click(), 200);
    }
  }, [autoScanMenu]);

  // Auto-open the Phrasebook sheet when arriving from the "Quick Phrases"
  // home shortcut. Mirrors autoScanMenu pattern.
  const autoPhrasebookRef = useRef(false);
  useEffect(() => {
    if (autoOpenPhrasebook && !autoPhrasebookRef.current) {
      autoPhrasebookRef.current = true;
      setTimeout(() => {
        const btn = document.querySelector('[data-testid="phrasebook-open"]');
        if (btn) btn.click();
      }, 250);
    }
  }, [autoOpenPhrasebook]);

  const srcLang = activeSide === "me" ? myLang : theirLang;
  const tgtLang = activeSide === "me" ? theirLang : myLang;

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const pushMessage = useCallback((partial) => {
    const msg = {
      id: partial.id || `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      side: activeSide,
      src: srcLang,
      tgt: tgtLang,
      ...partial,
    };
    // Functional update so we don't depend on `messages` in the deps
    // array (which would invalidate the callback on every render).
    // `hasUserSent` decides whether to drop the SEED_BUBBLES on first
    // user message.
    setMessages((prev) => (hasUserSent ? [...prev, msg] : [msg]));
    setHasUserSent(true);
    // Log into the recent-translations activity feed.
    if (msg.source && msg.translation) {
      addHistory("translate", {
        source: msg.source,
        translation: msg.translation,
        src: msg.src,
        tgt: msg.tgt,
      });
    }
  }, [activeSide, srcLang, tgtLang, hasUserSent]);

  // When the user toggles Male/Female in Settings, immediately invalidate
  // all in-memory bubble audio + the on-disk phrasebook audio cache so the
  // next speaker tap fetches fresh audio in the new voice. Without this,
  // tapping speaker on a bubble translated under the old voice plays the
  // wrong gender — which the user perceives as "Male voice broken."
  useEffect(() => {
    const unsub = subscribeTtsVoiceChange(() => {
      stopNativeTTS();
      try {
        const audio = audioElRef.current;
        if (audio) {
          audio.pause();
          audio.src = "";
        }
      } catch { /* element disposed — ignore */ }
      setMessages((list) =>
        list.map((m) => ({ ...m, audio_base64: null, audio_voice: null }))
      );
      clearCachedAudio();
    });
    return unsub;
  }, []);

  // History sheet state
  const [historyOpen, setHistoryOpen] = useState(false);

  // ---------------- TEXT TRANSLATION ----------------
  async function sendText(overrideText) {
    // `onClick={sendText}` in older code paths could pass the React
    // SyntheticEvent in as `overrideText`. Guard against any non-string
    // input so `.trim()` never crashes the keyboard send flow.
    const candidate = typeof overrideText === "string" ? overrideText : text;
    const raw = (candidate || "").trim();
    if (!raw) return;
    // Client-side quota gate: if the user is out of free text
    // translations, open the paywall IMMEDIATELY without a round trip
    // to the backend. Saves the user from typing → tapping Send →
    // waiting for a 402 → seeing the paywall pop. Cached translations
    // (already paid for) AND bundled phrasebook phrases (quota-exempt
    // server-side) still go through.
    const cachedCheck = getCached(raw, srcLang, tgtLang, provider, getOpenAIVoiceId());
    const hasFreshCache = cachedCheck && isValidTranslation(cachedCheck.translation, raw, tgtLang);
    const isPhrasebookSeed = isSeedPhrase(raw);
    if (translateLimitReached && !hasFreshCache && !isPhrasebookSeed) {
      // Build the same payload shape /translate's 402 returns so the
      // existing paywall handler renders the right copy.
      handle402({
        response: {
          status: 402,
          data: {
            detail: {
              error: "limit_reached",
              kind: "translate_text",
              message: "You've hit the daily text translation limit on the free plan",
            },
          },
        },
      });
      return;
    }
    // Claim the user-gesture window for audio so the upcoming TTS play
    // (after an async fetch) doesn't get blocked by iOS WKWebView.
    unlockAudio();
    stopNativeTTS();
    setText("");
    setShowKeyboard(false);
    setBusy(true);

    // Reject stale cache entries (translation that doesn't match the
    // target script — typically from a previous broken API response).
    // We fall through to the API which is now hardened with retry +
    // script validation.
    const cached = cachedCheck;
    if (cached && isValidTranslation(cached.translation, raw, tgtLang)) {
      pushMessage({
        source: raw,
        translation: cached.translation,
        audio_base64: cached.audio_base64,
        audio_voice: cached.audio_voice || null,
        cached: true,
      });
      if (cached.audio_base64) playB64(cached.audio_base64);
      else playTTS(cached.translation);
      setBusy(false);
      return;
    }

    const offline = getOfflineTranslation(raw, srcLang, tgtLang);
    if (offline) {
      pushMessage({ source: raw, translation: offline.translation, cached: true });
      setCached(raw, srcLang, tgtLang, provider, {
        translation: offline.translation,
        audio_base64: null,
      });
      playTTS(offline.translation);
      setBusy(false);
      return;
    }

    try {
      const r = await api.post(`/translate`, {
        text: raw,
        source_lang: srcLang,
        target_lang: tgtLang,
        provider,
        session_id: sessionIdRef.current,
      });
      sessionIdRef.current = r.data.session_id;
      pushMessage({
        id: r.data.id,
        source: raw,
        translation: r.data.translated_text,
      });

      // Background audio fetch — never blocks the UI and never throws
      // a "Translation failed" toast (the translation itself succeeded
      // by this point). If TTS is slow or fails, the bubble's speaker
      // icon stays tappable so the user can retry on demand. We capture
      // the voice ID at request time so the cache + bubble can be
      // invalidated if the user toggles Male/Female before tapping
      // speaker again.
      const voiceAtRequest = getOpenAIVoiceId();
      fetchTTS(r.data.translated_text)
        .then((audio) => {
          if (!audio) return;
          setMessages((list) =>
            list.map((m, i) =>
              i === list.length - 1
                ? { ...m, audio_base64: audio, audio_voice: voiceAtRequest }
                : m
            )
          );
          // Only auto-play if the user hasn't toggled gender mid-request.
          if (voiceAtRequest === getOpenAIVoiceId()) playB64(audio);
          setCached(raw, srcLang, tgtLang, provider, {
            translation: r.data.translated_text,
            audio_base64: audio,
            audio_voice: voiceAtRequest,
          });
        })
        .catch(() => {
          // Cache the translation without audio so subsequent identical
          // requests still hit the cache and avoid the translate API.
          setCached(raw, srcLang, tgtLang, provider, {
            translation: r.data.translated_text,
            audio_base64: null,
          });
        });
    } catch (e) {
      if (!handle402(e)) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const isTimeout =
          e?.code === "ECONNABORTED" ||
          e?.message?.includes("timeout") ||
          status === 504;
        let msg;
        if (status === 402) {
          // Defensive: 402 should normally be caught by handle402 above,
          // but if the response payload is malformed (no `error` field)
          // we surface the limit explicitly so the user isn't left
          // staring at "Translating..." with no feedback.
          msg =
            detail?.message ||
            "You've hit today's free translation limit. Sign in or upgrade to keep translating.";
        } else if (isTimeout) {
          // Don't blame the user's network — the slow tail is almost
          // always Gemini, not the device's signal. Phrasebook works
          // offline once a phrase has been translated once, so steer
          // them there as a reliable fallback.
          msg = "Server's a bit busy. Tap send again, or use Phrasebook for instant offline translations.";
        } else if (status >= 500 && status < 600) {
          msg = "Translation service is busy. Please tap send again in a moment.";
        } else if (typeof detail === "string") {
          msg = detail;
        } else if (e?.message?.includes("Network")) {
          msg = "Can't reach TipLingo. Check your connection and try again.";
        } else {
          msg = "Translation failed. Please try again.";
        }
        toast.error(msg);
      }
    } finally {
      // BULLETPROOF: setBusy(false) ALWAYS fires — even if handle402
      // throws, even if the toast call throws, even if React unmounted
      // the component mid-request. The "Translating…" pill can NEVER
      // get stuck forever again. The previous code put setBusy(false)
      // in the success AND catch branches without a finally, so any
      // unexpected throw would leave busy=true permanently.
      setBusy(false);
      // refreshAuth() is best-effort (updates /usage badge); if it
      // throws asynchronously after this point, it can never get the
      // user stuck because setBusy already fired.
      try { refreshAuth(); } catch { /* non-fatal */ }
    }
  }

  // ---------------- VOICE TRANSLATION ----------------
  async function uploadVoice(blob) {
    setBusy(true);
    try {
      const fd = new FormData();
      const ext =
        (blob.type.includes("mp4") && "mp4") ||
        (blob.type.includes("ogg") && "ogg") ||
        "webm";
      fd.append("audio", blob, `clip.${ext}`);
      fd.append("source_lang", srcLang);
      fd.append("target_lang", tgtLang);
      fd.append("provider", provider);
      fd.append("voice", getOpenAIVoiceId());
      if (sessionIdRef.current) fd.append("session_id", sessionIdRef.current);

      const voiceAtRequest = getOpenAIVoiceId();
      const r = await api.post(`/translate/voice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      pushMessage({
        id: r.data.id,
        source: r.data.transcription,
        translation: r.data.translation,
        audio_base64: r.data.audio_base64,
        audio_voice: voiceAtRequest,
      });
      setCached(r.data.transcription, srcLang, tgtLang, provider, {
        translation: r.data.translation,
        audio_base64: r.data.audio_base64,
        audio_voice: voiceAtRequest,
      });
      playB64(r.data.audio_base64);
    } catch (e) {
      if (!handle402(e)) {
        toast.error(e?.response?.data?.detail || "Voice translation failed");
      }
    } finally {
      setBusy(false);
      refreshAuth();
    }
  }

  const { recording, toggleRecording } = useVoiceRecorder({
    onBlob: uploadVoice,
    disabled: busy,
  });

  async function fetchTTS(speech) {
    try {
      // Voice ID maps to the user's gender preference in Settings → Voice
      // (defaults to "nova" / female). The backend `/tts` endpoint accepts
      // any of OpenAI's voice IDs.
      const r = await api.post(`/tts`, {
        text: speech,
        voice: getOpenAIVoiceId(),
      });
      return r.data.audio_base64;
    } catch {
      return null;
    }
  }

  // Reusable Audio element — created lazily, but ALWAYS the same instance
  // for the lifetime of the panel so iOS's "unlock" sticks across calls.
  function getAudioEl() {
    if (!audioElRef.current) {
      audioElRef.current = new Audio();
      // preload="auto" lets iOS know we're using it, improves first-play
      // reliability on cellular.
      audioElRef.current.preload = "auto";
    }
    return audioElRef.current;
  }

  // Synchronously claim the user-gesture window for our Audio element so
  // subsequent programmatic .play() calls (after async TTS fetches) work
  // on iOS WKWebView and mobile Safari. Idempotent — only runs once.
  // Must be called from a real user-interaction handler (onClick).
  function unlockAudio() {
    if (audioUnlockedRef.current) return;
    try {
      const audio = getAudioEl();
      // ~36 bytes of valid silent MP3 (decoded by every browser).
      audio.src =
        "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQwAAAAAAAAAAAAAAAAAAAAAAA";
      audio.volume = 0;
      const p = audio.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          audioUnlockedRef.current = true;
        }).catch(() => {
          // Some browsers reject the play() but the element is still
          // unlocked enough for subsequent plays. Mark unlocked anyway —
          // worst case the real audio call also fails and the user can
          // tap the speaker again with the unlock already in place.
          audioUnlockedRef.current = true;
        });
      } else {
        audioUnlockedRef.current = true;
      }
    } catch {
      // Same idea — flag as attempted so we don't keep retrying.
      audioUnlockedRef.current = true;
    }
  }

  function playB64(b64) {
    if (!b64) return;
    const audio = getAudioEl();
    audio.volume = 1;
    audio.src = `data:audio/mp3;base64,${b64}`;
    audio.playbackRate = getTtsSpeed();
    audio.play().catch(() => {
      // Last-ditch retry — sometimes the very first play after unlock
      // needs one more attempt on iOS. Quick & quiet, no toast.
      setTimeout(() => {
        try {
          audio.play().catch(() => {});
        } catch {
          /* element disposed before retry — ignore */
        }
      }, 50);
    });
  }

  // Tap-driven play: uses the device's native TTS for instant, offline-
  // friendly playback. Native TTS runs synchronously inside the user
  // gesture (no awaited fetch in between), so iOS WKWebView's autoplay
  // policy never blocks it. We attempt to also kick off a background
  // /tts fetch so the higher-quality OpenAI voice is cached for next
  // time, but that's a "nice to have" — the user always hears something
  // immediately. `messageId` lets us patch the higher-quality audio
  // back onto the bubble when it eventually arrives.
  async function playTTS(speech, messageId, langOverride) {
    if (!speech) return;
    unlockAudio();
    const lang = langOverride || tgtLang;
    // Voice quality lock: PREMIUM (default) always uses OpenAI TTS so the
    // user never hears an iOS novelty/compact voice ("dying old man"
    // bug). FAST plays the device's native voice immediately and only
    // falls back to OpenAI when native can't find a safe voice.
    const mode = getTtsPlaybackMode();
    const voiceAtRequest = getOpenAIVoiceId();
    let spoke = false;
    if (mode === "fast") {
      spoke = speakNative(speech, lang, getTtsSpeed());
    }
    // Background: pull a higher-quality OpenAI render. In premium mode
    // this is the ONLY playback path. In fast mode it's a "next time"
    // upgrade that gets cached.
    fetchTTS(speech)
      .then((b64) => {
        if (!b64) return;
        if (messageId) {
          setMessages((list) =>
            list.map((m) =>
              m.id === messageId
                ? { ...m, audio_base64: b64, audio_voice: voiceAtRequest }
                : m,
            ),
          );
        }
        // In premium mode, always play. In fast mode, only play if
        // native didn't speak (voice unavailable or blocked). Skip if
        // the user toggled gender during the fetch (a fresh request
        // will fire from the next speaker tap).
        const shouldPlay =
          (mode === "premium" || !spoke) &&
          voiceAtRequest === getOpenAIVoiceId();
        if (shouldPlay) playB64(b64);
      })
      .catch(() => {
        /* TTS unavailable — fast-mode native may have already played */
      });
  }

  function favoriteMsg(msg) {
    toggleFavorite({
      source: msg.source,
      translation: msg.translation,
      src: msg.src,
      tgt: msg.tgt,
      provider,
    });
    setFavTick((v) => v + 1);
  }

  const swapLangs = () => {
    setMyLang(theirLang);
    setTheirLang(myLang);
  };

  // ---------------- MENU SCAN ----------------
  async function onPickMenuImage(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please upload an image");
      return;
    }
    const dataUrl = await resizeToDataUrl(file, 1600, 0.82);
    setMenuImgUrl(dataUrl);
    setMenuScanning(true);
    setMenuPinnedIdx(null);
    setMenuHoverIdx(null);
    setMenuResult(null);
    try {
      const b64 = dataUrl.split(",", 2)[1];
      const r = await api.post(`/menu/scan`, {
        image_base64: b64,
        target_lang: myLang,
        mime: "image/jpeg",
      });
      if (!r.data.items?.length) {
        toast.message("Couldn't read any items — try a clearer photo");
      } else {
        const withBox = r.data.items.filter((i) => i.bbox).length;
        toast.success(`Found ${r.data.items.length} items (${withBox} mapped)`);
      }
      setMenuResult(r.data);
    } catch (err) {
      if (!handle402(err)) {
        toast.error(err?.response?.data?.detail || "Scan failed");
      }
      setMenuImgUrl(null);
    } finally {
      setMenuScanning(false);
      refreshAuth();
    }
  }

  function closeMenuScan() {
    setMenuResult(null);
    setMenuImgUrl(null);
    setMenuScanning(false);
    setMenuPinnedIdx(null);
    setMenuHoverIdx(null);
  }

  function speakItemTranslation(itemText) {
    playTTS(itemText);
  }

  const menuItemsWithBbox = (menuResult?.items || []).filter(
    (it) => Array.isArray(it.bbox) && it.bbox.length === 4
  );
  const menuItemsWithoutBbox = (menuResult?.items || []).filter(
    (it) => !Array.isArray(it.bbox) || it.bbox.length !== 4
  );
  const menuActiveIdx = menuPinnedIdx ?? menuHoverIdx;

  return (
    <div className="min-h-screen bg-white pb-[300px]" data-testid="translate-panel">
      {/* Header */}
      <div className="px-5 pt-4 flex items-center justify-between">
        <button
          type="button"
          data-testid="translate-back-btn"
          onClick={onBack}
          aria-label="Back"
          className="w-10 h-10 rounded-full bg-white shadow-soft border border-navy/10 flex items-center justify-center"
        >
          <CaretLeft size={20} weight="bold" color="#0E2C54" />
        </button>
        <h2 className="font-jakarta font-extrabold text-navy text-xl">
          Translate
        </h2>
        <button
          type="button"
          aria-label="History"
          data-testid="translate-history-btn"
          onClick={() => setHistoryOpen(true)}
          className="w-10 h-10 rounded-full bg-white shadow-soft border border-navy/10 flex items-center justify-center"
        >
          <Clock size={20} weight="duotone" color="#0E2C54" />
        </button>
      </div>

      {/* Scan a menu pill */}
      <div className="mx-5 mt-5 bg-white rounded-2xl shadow-soft border border-navy/10 px-4 py-3 flex items-center justify-between">
        <button
          type="button"
          data-testid="scan-menu-link"
          onClick={() => menuFileRef.current?.click()}
          disabled={menuScanning}
          className="flex items-center gap-2 disabled:opacity-60"
        >
          {menuScanning ? (
            <CircleNotch size={22} weight="bold" color="#E55B2C" className="animate-spin" />
          ) : (
            <CookingPot size={22} weight="duotone" color="#E55B2C" />
          )}
          <span className="text-orange font-jakarta font-bold text-base">
            {menuScanning ? "Reading menu…" : (
              <>
                Scan a menu
              </>
            )}
          </span>
          {!menuScanning && (
            <>
              <ArrowRight size={14} weight="bold" color="#4A5D78" />
              <span className="font-jakarta font-semibold text-navy-soft text-sm">
                {myLang}
              </span>
            </>
          )}
        </button>
        <div
          className="flex items-center gap-1 text-forest text-[11px] font-jakarta font-bold uppercase tracking-wider"
          data-testid="menu-scan-quota-badge"
        >
          <SparkleLeaf />
          {menuQuotaLabel || "—"}
        </div>
        <input
          ref={menuFileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onPickMenuImage}
          data-testid="menu-file-input"
        />
      </div>

      {/* Language pickers — underline style, no card chrome */}
      <div className="mx-5 mt-6 grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
        <div className="relative">
          <div className="text-navy-soft text-xs font-jakarta font-semibold">
            You speak
          </div>
          <div className="mt-1 flex items-center justify-between border-b border-navy/30 pb-1.5">
            <span className="font-jakarta font-bold text-navy text-lg">
              {myLang}
            </span>
            <CaretDown size={14} weight="bold" color="#0E2C54" />
          </div>
          <select
            data-testid="my-lang"
            value={myLang}
            onChange={(e) => setMyLang(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
          >
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={swapLangs}
          aria-label="Swap"
          className="w-10 h-10 rounded-full bg-navy text-white shadow-soft flex items-center justify-center mb-1"
        >
          <ArrowsDownUp size={16} weight="bold" />
        </button>

        <div className="relative">
          <div className="text-navy-soft text-xs font-jakarta font-semibold">
            They speak
          </div>
          <div className="mt-1 flex items-center justify-between border-b border-navy/30 pb-1.5">
            <span className="font-jakarta font-bold text-navy text-lg">
              {theirLang}
            </span>
            <CaretDown size={14} weight="bold" color="#0E2C54" />
          </div>
          <select
            data-testid="their-lang"
            value={theirLang}
            onChange={(e) => setTheirLang(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
          >
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Me / Them */}
      <div className="mx-5 mt-4 grid grid-cols-2 gap-3">
        <button
          type="button"
          data-testid="side-me"
          onClick={() => setActiveSide("me")}
          className={`py-3 rounded-full text-base font-jakarta font-bold transition-colors ${
            activeSide === "me"
              ? "bg-navy text-white shadow-soft"
              : "bg-white text-navy border border-navy/15"
          }`}
        >
          Me ({myLang})
        </button>
        <button
          type="button"
          data-testid="side-them"
          onClick={() => setActiveSide("them")}
          className={`py-3 rounded-full text-base font-jakarta font-bold transition-colors ${
            activeSide === "them"
              ? "bg-navy text-white shadow-soft"
              : "bg-white text-navy border border-navy/15"
          }`}
        >
          Them ({theirLang})
        </button>
      </div>

      {/* Conversation */}
      <div className="px-5 mt-6">
        <div ref={scrollerRef} data-testid="conversation" className="space-y-4">
          {messages.map((msg) => (
            <Bubble
              key={msg.id}
              msg={msg}
              onSpeakSource={() => {
                unlockAudio();
                playTTS(msg.source, null, msg.src || srcLang);
              }}
              onSpeak={() => {
                unlockAudio();
                // If the cached audio was rendered with a DIFFERENT voice
                // (user toggled Male/Female since), refetch a fresh one
                // in the current voice so the user always hears the
                // gender they selected. Falls back to native TTS while
                // the new audio is in flight.
                const currentVoice = getOpenAIVoiceId();
                if (msg.audio_base64 && msg.audio_voice === currentVoice) {
                  playB64(msg.audio_base64);
                } else {
                  playTTS(msg.translation, msg.id, msg.tgt || tgtLang);
                }
              }}
              onFav={() => favoriteMsg(msg)}
              isFav={isFavorite(msg.source, msg.translation)}
              tick={favTick}
            />
          ))}
          {busy && (
            <div className="bg-bone/60 rounded-2xl p-4 text-center text-navy-soft text-sm animate-pulse font-jakarta">
              Translating…
            </div>
          )}
        </div>

        {/* Inline keyboard drawer — docked directly below the bubble list
            with a small gap (≈ half the bubble height) so the input
            visually belongs to the conversation instead of floating
            mid-screen. Sticky-positions itself so it stays visible when
            the iOS keyboard pushes the view. */}
        {showKeyboard && (
          <div className="sticky bottom-4 mt-14 z-20">
            <div className="bg-white rounded-2xl shadow-deep border border-navy/10 p-3 flex items-center gap-2">
              <input
                data-testid="translate-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendText()}
                autoFocus
                placeholder={`Type in ${srcLang}…`}
                className="flex-1 bg-bone rounded-xl px-3 py-2.5 text-base text-navy outline-none border border-navy/10"
              />
              <button
                type="button"
                data-testid="translate-send-btn"
                onClick={() => sendText()}
                disabled={!text.trim() || busy}
                className="w-11 h-11 rounded-full bg-orange text-white shadow-soft flex items-center justify-center disabled:opacity-40"
                aria-label="Send"
              >
                <PaperPlaneRight size={18} weight="fill" />
              </button>
              <button
                type="button"
                onClick={() => setShowKeyboard(false)}
                aria-label="Close"
                className="w-11 h-11 rounded-full bg-white border border-navy/10 text-navy-soft flex items-center justify-center"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </div>


      {/* Phrasebook pill — sits above the bottom action row.
          Hidden while the keyboard drawer is open so the floating pill
          doesn't overlap the type-in input + Send button.
          NOTE on pointer-events: the outer fixed div AND the flex column
          stay `pointer-events-none` so they NEVER intercept taps on
          bubble content underneath (especially speaker icons that may
          horizontally align with the pill). Only the PhraseBook pill
          itself opts back in via `pointer-events-auto` on its own
          interactive element. The "works offline" caption is purely
          decorative and must never eat taps. */}
      {!showKeyboard && (
        <div className="fixed bottom-[200px] left-1/2 -translate-x-1/2 w-full max-w-md px-5 z-20 pointer-events-none">
          <div className="flex flex-col items-center gap-1">
            <div className="pointer-events-auto">
              <PhraseBook
                myLang={myLang}
                theirLang={theirLang}
                provider={provider}
                onUse={(phrase) => sendText(phrase)}
                onPlayFavorite={(f) => {
                  const currentVoice = getOpenAIVoiceId();
                  if (f.audio_base64 && f.audio_voice === currentVoice) {
                    playB64(f.audio_base64);
                  } else {
                    playTTS(f.translation);
                  }
                }}
                refreshKey={favTick}
              />
            </div>
            <span className="text-navy-soft text-[10px] uppercase tracking-widest font-jakarta font-bold">
              works offline after first use
            </span>
          </div>
        </div>
      )}

      {/* Bottom action row — pill buttons + big orange mic.
          Hidden while the keyboard drawer is open (the drawer takes its
          place in the same fixed-bottom slot — see above). */}
      {!showKeyboard && (
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md px-5 pb-24 pt-4 z-10 bg-gradient-to-t from-white via-white/95 to-transparent">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              data-testid="speak-btn"
              onClick={toggleRecording}
              className="flex-1 max-w-[120px] bg-white border border-navy/15 rounded-full py-3 flex items-center justify-center gap-2 font-jakarta font-bold text-navy text-sm shadow-soft"
            >
              <SpeakerHigh size={18} weight="duotone" color="#0E2C54" />
              Speak
            </button>

            <button
              type="button"
              data-testid="mic-btn"
              onClick={toggleRecording}
              className={`w-20 h-20 rounded-full bg-orange shadow-deep flex items-center justify-center transition-all ${
                recording ? "recording-ring scale-110" : "hover:bg-orange-hover"
              }`}
              aria-label={recording ? "Stop recording" : "Start recording"}
            >
              {recording ? (
                <Stop size={32} weight="fill" color="#fff" />
              ) : (
                <Microphone size={34} weight="fill" color="#fff" />
              )}
            </button>

            <button
              type="button"
              data-testid="keyboard-btn"
              onClick={() => setShowKeyboard((v) => !v)}
              className="flex-1 max-w-[120px] bg-white border border-navy/15 rounded-full py-3 flex items-center justify-center gap-2 font-jakarta font-bold text-navy text-sm shadow-soft"
            >
              <Keyboard size={18} weight="duotone" color="#0E2C54" />
              Keyboard
            </button>
          </div>
        </div>
      )}

      {/* Recent translations sheet */}
      <HistorySheet
        kind="translate"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onReuse={(entry) => {
          // Pre-fill the keyboard area with the source text and open keyboard
          setText(entry.source);
          setShowKeyboard(true);
        }}
      />


      {/* Menu Scan results modal */}
      {(menuScanning || menuResult || menuImgUrl) && (
        <div
          className="fixed inset-0 z-40 bg-navy/40 backdrop-blur-sm flex items-start justify-center p-3 pt-6"
          data-testid="menu-scan-modal"
          onClick={closeMenuScan}
        >
          <div
            className="w-full max-w-md bg-white rounded-3xl shadow-deep max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white z-10 px-5 pt-4 pb-3 border-b border-navy/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CookingPot size={22} weight="duotone" color="#E55B2C" />
                <h3 className="font-jakarta font-extrabold text-navy text-lg">
                  {menuScanning ? "Reading menu…" : menuResult?.title || "Menu translation"}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeMenuScan}
                data-testid="menu-modal-close"
                aria-label="Close"
                className="w-9 h-9 rounded-full bg-bone/80 border border-navy/10 flex items-center justify-center"
              >
                <X size={18} weight="bold" color="#0E2C54" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {menuResult?.source_language && (
                <div className="flex items-center justify-center gap-2 text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider">
                  <span>{menuResult.source_language}</span>
                  <ArrowRight size={12} weight="bold" color="#4A5D78" />
                  <span>{myLang}</span>
                </div>
              )}

              {menuScanning && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <CircleNotch size={32} weight="bold" color="#E55B2C" className="animate-spin" />
                  <div className="font-jakarta font-bold text-navy text-base">
                    Translating your menu…
                  </div>
                  <div className="text-navy-soft text-xs font-jakarta">
                    This usually takes a couple of seconds
                  </div>
                </div>
              )}

              {!menuScanning && menuResult && menuImgUrl && (
                <>
                  <div className="text-[11px] uppercase tracking-widest text-navy-soft font-jakarta font-bold flex items-center gap-2">
                    <MagicWand size={12} weight="fill" color="#E55B2C" />
                    Tap any highlighted item for its translation
                  </div>

                  <MenuOverlay
                    imgUrl={menuImgUrl}
                    items={menuItemsWithBbox}
                    activeIdx={menuActiveIdx}
                    onHover={setMenuHoverIdx}
                    onPin={setMenuPinnedIdx}
                    onSpeak={speakItemTranslation}
                  />

                  {menuItemsWithoutBbox.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-navy-soft font-jakarta font-bold flex items-center gap-2">
                        <span className="h-px flex-1 bg-navy/10" />
                        Other items
                        <span className="h-px flex-1 bg-navy/10" />
                      </div>
                      {menuItemsWithoutBbox.map((it, i) => (
                        <MenuFallbackRow
                          key={`fb-${it.original}-${i}`}
                          item={it}
                          onSpeak={speakItemTranslation}
                        />
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => menuFileRef.current?.click()}
                    data-testid="menu-scan-another"
                    className="w-full mt-2 bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3.5 shadow-soft flex items-center justify-center gap-2"
                  >
                    <Camera size={18} weight="fill" color="#fff" />
                    Scan another menu
                  </button>
                </>
              )}

              {!menuScanning && !menuResult && menuImgUrl && (
                <div className="flex flex-col items-center gap-3 py-6">
                  <div className="text-navy font-jakarta font-bold text-base text-center">
                    We couldn&apos;t read that menu — try a clearer, brighter photo.
                  </div>
                  <button
                    type="button"
                    onClick={() => menuFileRef.current?.click()}
                    className="bg-orange text-white font-jakarta font-extrabold text-sm rounded-full px-5 py-2.5"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Bubble({ msg, onSpeak, onSpeakSource, onFav, isFav, tick }) {
  void tick; // ensure re-render
  return (
    <div className="bg-bone/60 rounded-2xl px-4 py-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0 text-navy font-jakarta font-semibold text-base">
          {msg.source}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={onSpeakSource}
            aria-label="Speak source"
            className="w-8 h-8 flex items-center justify-center"
          >
            <SpeakerHigh size={20} weight="duotone" color="#0E2C54" />
          </button>
          <button
            type="button"
            onClick={onFav}
            aria-label="Save"
            className="w-8 h-8 flex items-center justify-center"
          >
            <BookmarkSimple
              size={20}
              weight={isFav ? "fill" : "duotone"}
              color={isFav ? "#E55B2C" : "#0E2C54"}
            />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0 text-forest font-jakarta font-semibold text-base">
          {msg.translation}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={onSpeak}
            aria-label="Speak translation"
            className="w-8 h-8 flex items-center justify-center"
          >
            <SpeakerHigh size={20} weight="duotone" color="#2E7D5A" />
          </button>
          <button
            type="button"
            onClick={onFav}
            aria-label="Save translation"
            data-testid="phrase-fav-btn"
            className="w-8 h-8 flex items-center justify-center"
          >
            <BookmarkSimple
              size={20}
              weight={isFav ? "fill" : "duotone"}
              color={isFav ? "#E55B2C" : "#0E2C54"}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

function SparkleLeaf() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 2 L9.6 6.4 L14 8 L9.6 9.6 L8 14 L6.4 9.6 L2 8 L6.4 6.4 Z"
        fill="#2E7D5A"
      />
    </svg>
  );
}

// Pull the most recent translation-history entries and adapt them into
// the bubble shape the chat list renders. Most-recent appears at the
// bottom (so the conversation reads top-down, oldest → newest), which
// matches the rest of the chat UI. Returns [] if there's no history
// yet — leaves a clean empty state instead of locale-mismatched seeds.
//
// When `mySrc` + `theirTgt` are passed, the result is filtered to ONLY
// entries that match the current language pair. This prevents stale
// French bubbles from showing when the user has switched the target to
// Korean (which was a bug surfacing as "translation broken" since users
// confused the stale bubble for the system's response).
function recentBubblesFromHistory(limit = 2, mySrc, theirTgt) {
  try {
    let recent = listHistory("translate");
    if (mySrc && theirTgt) {
      const src = (mySrc || "").toLowerCase();
      const tgt = (theirTgt || "").toLowerCase();
      recent = recent.filter((h) => {
        const hs = (h.src || "").toLowerCase();
        const ht = (h.tgt || "").toLowerCase();
        // Accept the pair in either direction (handles the case where the
        // user flipped activeSide partway through the previous session).
        return (
          (hs === src && ht === tgt) || (hs === tgt && ht === src)
        );
      });
    }
    return recent
      .slice(0, limit)
      .filter((h) => h && h.source && h.translation)
      .map((h, i) => ({
        id: h.id || `recent-${i}`,
        side: "me",
        src: h.src || "English",
        tgt: h.tgt || "",
        source: h.source,
        translation: h.translation,
      }))
      .reverse();
  } catch {
    return [];
  }
}
